import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Avatar from '../../../components/base/Avatar';
import Button from '../../../components/base/Button';
import EmailLayout from '../../../layouts/EmailLayout';
import { Card, Col, Dropdown, Row, Spinner, Alert } from 'react-bootstrap';
// @ts-ignore
import team60 from '../../../assets/img/team/60.webp';
// @ts-ignore
import generic41 from '../../../assets/img/generic/41.png';
import RevealDropdown, {
  RevealDropdownTrigger
} from '../../../components/base/RevealDropdown';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import TooltipIconButton from '../../../components/common/TooltipIconButton';
import AttachmentPreview from '../../../components/common/AttachmentPreview';
import {
  faAngleLeft,
  faArchive,
  faPaperclip,
  faPrint,
  faReply,
  faReplyAll,
  faShare,
  faTrashCan
} from '@fortawesome/free-solid-svg-icons';
import { faStar } from '@fortawesome/free-regular-svg-icons';
import { faStar as farStar } from '@fortawesome/free-regular-svg-icons';

// 🔧 IMPORTAR SERVICIOS
import { deleteEmails, moveEmails, toggleEmailStar } from '../../../service/emailActionsService';

interface EmailDetailData {
  id: number;
  sender: {
    name: string;
    email: string;
    fullAddress: string;
  };
  recipient: {
    to?: string;
    cc?: string;
  };
  subject: string;
  date: Date;
  formattedDate: string;
  body: {
    text: string;
    html: string;
  };
  starred: boolean;
  read: boolean;
  attachments: Array<{
    id: number;
    name: string;
    size: string;
    format: string;
    contentType: string;
  }>;
}

interface EmailDetailProps {
  emailId?: number;
  folderPath?: string;
  embedded?: boolean;
}

const EmailDetail = ({ emailId: propEmailId, folderPath: propFolderPath, embedded = false }: EmailDetailProps) => {
  const { emailId: paramEmailId, folderPath: paramFolderPath } = useParams<{ emailId?: string; folderPath?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState<EmailDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [starred, setStarred] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Usar props si están disponibles, sino usar params/state
  const currentEmailId = propEmailId || paramEmailId || (location.state as any)?.emailId;
  const currentFolderPath = propFolderPath || paramFolderPath || (location.state as any)?.folderPath || 'INBOX';

  // Obtener configuración de email
  const getEmailConfig = () => {
    const savedConfig = localStorage.getItem('emailConfig');
    return savedConfig ? JSON.parse(savedConfig) : null;
  };

  // 🔧 FUNCIÓN MEJORADA PARA VOLVER
  const handleBack = () => {
    console.log(`🔙 Navigating back from folder: ${currentFolderPath}`);
    
    // Si la carpeta actual es INBOX, navegar directamente a inbox
    if (currentFolderPath === 'INBOX' || currentFolderPath === 'inbox') {
      navigate('/anubis/email/inbox');
    } else {
      // Para otras carpetas, navegar a la ruta específica de la carpeta
      navigate(`/anubis/email/folder/${encodeURIComponent(currentFolderPath)}`);
    }
  };

  // Cargar email cuando cambie el ID
  useEffect(() => {
    if (currentEmailId) {
      loadEmailDetail();
    }
  }, [currentEmailId, currentFolderPath]);

  const loadEmailDetail = async () => {
    const config = getEmailConfig();
    
    if (!config) {
      setError('No hay configuración de email. Configure su cuenta primero.');
      setLoading(false);
      return;
    }

    if (!currentEmailId) {
      setError('No se especificó un email para mostrar');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('https://backend.anubisai.net/api/pmail/get-email-detail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          server: config.server,
          username: config.username,
          password: config.password,
          imapPort: config.imapPort,
          ssl: config.ssl,
          folderPath: decodeURIComponent(currentFolderPath),
          emailUid: parseInt(currentEmailId.toString())
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const receivedEmail = result.email;
        if (!receivedEmail.recipient) {
          receivedEmail.recipient = { to: '', cc: '' };
        } else {
          if (receivedEmail.recipient.to === undefined) {
            receivedEmail.recipient.to = '';
          }
          if (receivedEmail.recipient.cc === undefined) {
            receivedEmail.recipient.cc = '';
          }
        }
        
        setEmail(receivedEmail);
        setStarred(receivedEmail.starred);
        setError(null);
      } else {
        setError(result.error || 'Error al cargar el email');
      }
    } catch (err) {
      console.error('Error loading email detail:', err);
      setError('Error de conexión al cargar el email');
    } finally {
      setLoading(false);
    }
  };

  // 🔧 FUNCIÓN PARA CREAR RESPUESTA/REENVÍO
  const createEmailDraft = (type: 'reply' | 'reply-all' | 'forward') => {
    console.log(`📝 Creating ${type} draft for email:`, email?.subject);
    
    if (!email) {
      console.log('❌ No email available for draft creation');
      return;
    }

    const config = getEmailConfig();
    if (!config) {
      console.log('❌ No email config available');
      alert('No se encontró configuración de email. Configure su cuenta primero.');
      return;
    }

    let to = '';
    let cc = '';
    let subject = '';
    let body = '';

    // Preparar datos según el tipo de acción
    switch (type) {
      case 'reply':
        to = email.sender.email;
        subject = email.subject.startsWith('Re: ') ? email.subject : `Re: ${email.subject}`;
        body = generateReplyBody(email, config);
        console.log('📧 Reply data prepared:', { to, subject });
        break;

      case 'reply-all':
        to = email.sender.email;
        // Agregar otros destinatarios al CC (excluyendo al usuario actual)
        const allRecipients = [
          email.recipient.to,
          email.recipient.cc
        ].filter(Boolean).join(', ');
        
        const recipients = allRecipients
          .split(',')
          .map(addr => addr.trim())
          .filter(addr => addr && addr !== config.username && addr !== email.sender.email);
        
        cc = recipients.join(', ');
        subject = email.subject.startsWith('Re: ') ? email.subject : `Re: ${email.subject}`;
        body = generateReplyBody(email, config);
        console.log('📧 Reply-all data prepared:', { to, cc, subject });
        break;

      case 'forward':
        to = '';
        subject = email.subject.startsWith('Fwd: ') ? email.subject : `Fwd: ${email.subject}`;
        body = generateForwardBody(email, config);
        console.log('📧 Forward data prepared:', { subject });
        break;
    }

    // Navegar al compose con datos prellenados
    console.log('🚀 Navigating to compose with state:', { to, cc, subject, action: type });
    navigate('/anubis/email/compose', {
      state: {
        to,
        cc,
        subject,
        body,
        originalEmail: email,
        action: type
      }
    });
  };

  // 🔧 GENERAR CUERPO DE RESPUESTA
  const generateReplyBody = (originalEmail: EmailDetailData, config: any): string => {
    const originalText = originalEmail.body.text || stripHtml(originalEmail.body.html);
    const date = new Date(originalEmail.date).toLocaleString('es-CL');
    
    return `


------- Mensaje original -------
De: ${originalEmail.sender.name} <${originalEmail.sender.email}>
Fecha: ${date}
Para: ${originalEmail.recipient.to}
Asunto: ${originalEmail.subject}

${originalText}`;
  };

  // 🔧 GENERAR CUERPO DE REENVÍO
  const generateForwardBody = (originalEmail: EmailDetailData, config: any): string => {
    const originalText = originalEmail.body.text || stripHtml(originalEmail.body.html);
    const date = new Date(originalEmail.date).toLocaleString('es-CL');
    
    return `


---------- Mensaje reenviado ----------
De: ${originalEmail.sender.name} <${originalEmail.sender.email}>
Fecha: ${date}
Para: ${originalEmail.recipient.to}
CC: ${originalEmail.recipient.cc || 'N/A'}
Asunto: ${originalEmail.subject}

${originalText}`;
  };

  // 🔧 LIMPIAR HTML
  const stripHtml = (html: string): string => {
    if (!html) return '';
    return html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .trim();
  };

  // 🔧 MANEJAR ACCIONES DE EMAIL CON SERVICIOS REALES
  const handleEmailAction = async (action: string) => {
    console.log(`🔘 Action clicked: ${action}`);
    
    if (!email) {
      console.log('❌ No email data available');
      return;
    }

    setActionLoading(action);

    try {
      switch (action) {
        case 'reply':
          console.log('📧 Creating reply draft...');
          createEmailDraft('reply');
          break;
        case 'reply-all':
          console.log('📧 Creating reply-all draft...');
          createEmailDraft('reply-all');
          break;
        case 'forward':
          console.log('📧 Creating forward draft...');
          createEmailDraft('forward');
          break;
        case 'star':
          console.log('⭐ Toggling star...');
          await handleToggleStar();
          break;
        case 'delete':
          console.log('🗑️ Handling delete...');
          await handleDelete();
          break;
        case 'archive':
          console.log('📦 Handling archive...');
          await handleArchive();
          break;
        case 'print':
          console.log('🖨️ Handling print...');
          handlePrint();
          break;
        default:
          console.log(`❓ Action ${action} not implemented yet`);
      }
    } catch (error) {
      console.error(`❌ Error handling ${action}:`, error);
      alert(`Error al ejecutar ${action}: ${error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  // 🔧 TOGGLE STAR CON BACKEND REAL
  const handleToggleStar = async () => {
    console.log('⭐ Toggling star from', starred, 'to', !starred);
    
    try {
      const result = await toggleEmailStar([email!.id], currentFolderPath, !starred);
      
      if (result.success) {
        setStarred(!starred);
        console.log('✅ Star toggled successfully');
        // Mostrar mensaje de éxito discreto
        const action = !starred ? 'destacado' : 'quitado de destacados';
        alert(`✅ Email ${action} correctamente`);
      } else {
        throw new Error(result.message);
      }
    } catch (error: any) {
      console.error('❌ Error toggling star:', error);
      alert(`❌ Error al cambiar destacado: ${error.message}`);
    }
  };

  // 🔧 ELIMINAR EMAIL CON BACKEND REAL - CORREGIDO PARA VOLVER A LA CARPETA CORRECTA
  const handleDelete = async () => {
    const isTrashFolder = currentFolderPath.toLowerCase().includes('trash') || 
                         currentFolderPath.toLowerCase().includes('papelera');
    
    const actionText = isTrashFolder ? 'eliminar permanentemente' : 'mover a papelera';
    const confirmMessage = `¿Estás seguro de que quieres ${actionText} este email?`;
    
    if (!confirm(confirmMessage)) return;
    
    try {
      const result = await deleteEmails([email!.id], currentFolderPath, isTrashFolder);
      
      if (result.success) {
        console.log('✅ Email deleted successfully');
        alert(`✅ ${result.message}`);
        
        // Navegar de vuelta a la carpeta correcta después de eliminar
        handleBack();
      } else {
        throw new Error(result.message);
      }
    } catch (error: any) {
      console.error('❌ Error deleting email:', error);
      alert(`❌ Error al eliminar: ${error.message}`);
    }
  };

  // 🔧 ARCHIVAR EMAIL CON BACKEND REAL - CORREGIDO PARA VOLVER A LA CARPETA CORRECTA
  const handleArchive = async () => {
    // Buscar carpeta de archivo (se podría mejorar obteniendo lista de carpetas)
    const archiveFolders = ['Archive', 'INBOX.Archive', 'Archivo', 'INBOX.Archivo'];
    const destinationFolder = 'Archive'; // Por defecto, se podría hacer más inteligente
    
    try {
      const result = await moveEmails([email!.id], currentFolderPath, destinationFolder);
      
      if (result.success) {
        console.log('✅ Email archived successfully');
        alert(`✅ ${result.message}`);
        
        // Navegar de vuelta a la carpeta correcta después de archivar
        handleBack();
      } else {
        throw new Error(result.message);
      }
    } catch (error: any) {
      console.error('❌ Error archiving email:', error);
      // Si falla el archivo automático, preguntar al usuario
      const tryManual = confirm('No se pudo archivar automáticamente. ¿Desea elegir una carpeta manualmente?');
      if (tryManual) {
        alert('Funcionalidad de mover a carpeta específica próximamente. Use el listado de emails para seleccionar carpeta.');
      }
    }
  };

  // 🔧 IMPRIMIR EMAIL
  const handlePrint = () => {
    console.log('🖨️ Starting print process...');
    
    if (!email) {
      console.log('❌ No email available for printing');
      return;
    }
    
    // Crear ventana de impresión con el contenido del email
    console.log('📄 Creating print window...');
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      console.log('❌ Failed to open print window');
      alert('No se pudo abrir la ventana de impresión. Verifique que no esté bloqueado por el navegador.');
      return;
    }

    const content = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Email - ${email.subject}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; }
            .header { border-bottom: 2px solid #333; padding-bottom: 15px; margin-bottom: 20px; }
            .subject { font-size: 20px; font-weight: bold; margin-bottom: 15px; color: #333; }
            .meta { color: #666; font-size: 14px; }
            .meta strong { color: #333; }
            .body { margin-top: 20px; font-size: 14px; }
            .attachments { margin-top: 20px; padding-top: 15px; border-top: 1px solid #ccc; }
            @media print { 
              body { margin: 0; font-size: 12px; }
              .header { border-bottom: 1px solid #000; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="subject">${email.subject}</div>
            <div class="meta">
              <strong>De:</strong> ${email.sender.name} &lt;${email.sender.email}&gt;<br>
              <strong>Para:</strong> ${email.recipient.to}<br>
              ${email.recipient.cc ? `<strong>CC:</strong> ${email.recipient.cc}<br>` : ''}
              <strong>Fecha:</strong> ${email.formattedDate}
            </div>
          </div>
          <div class="body">
            ${email.body.html || email.body.text.replace(/\n/g, '<br>')}
          </div>
          ${email.attachments.length > 0 ? `
            <div class="attachments">
              <strong>Adjuntos:</strong><br>
              ${email.attachments.map(att => `• ${att.name} (${att.size})`).join('<br>')}
            </div>
          ` : ''}
        </body>
      </html>
    `;

    console.log('📝 Writing content to print window...');
    printWindow.document.write(content);
    printWindow.document.close();
    
    console.log('🖨️ Triggering print dialog...');
    printWindow.print();
    
    console.log('✅ Print process completed');
  };

  const getInitial = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  // Contenido del email
  const emailContent = () => {
    if (loading) {
      return (
        <Card.Body className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </Card.Body>
      );
    }

    if (error || !email) {
      return (
        <Card.Body>
          <Alert variant="danger">
            {error || 'Email no encontrado'}
          </Alert>
          {!embedded && (
            <Button variant="primary" onClick={handleBack}>
              Volver a la bandeja
            </Button>
          )}
        </Card.Body>
      );
    }

    return (
      <>
        <Card.Header className="border-0">
          <div className="d-flex flex-between-center pb-3 border-bottom border-translucent">
            {!embedded && (
              <Button
                variant="link"
                className="p-0 text-body-secondary me-3"
                onClick={handleBack}
              >
                <FontAwesomeIcon
                  icon={faAngleLeft}
                  className="fw-bolder fs-8"
                />
              </Button>
            )}
            <h3 className="flex-1 mb-0 lh-sm line-clamp-1">
              {email.subject}
            </h3>

            <RevealDropdownTrigger>
              <RevealDropdown>
                <Dropdown.Item onClick={() => handleEmailAction('archive')}>
                  <FontAwesomeIcon icon={faArchive} className="me-2" />
                  Archivar
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleEmailAction('print')}>
                  <FontAwesomeIcon icon={faPrint} className="me-2" />
                  Imprimir
                </Dropdown.Item>
                <Dropdown.Item className="text-danger" onClick={() => handleEmailAction('delete')}>
                  <FontAwesomeIcon icon={faTrashCan} className="me-2" />
                  Eliminar
                </Dropdown.Item>
                <Dropdown.Item disabled>
                  Reportar abuso
                </Dropdown.Item>
              </RevealDropdown>
            </RevealDropdownTrigger>
          </div>
        </Card.Header>
        <Card.Body className="p-0 pb-4 scrollbar">
          <div className="email-detail-content px-4">
            <Row className="align-items-center gy-3 gx-2 mb-10">
              <Col
                xs={12}
                sm="auto"
                lg={12}
                xl="auto"
                className="d-flex gap-4 gap-lg-3 gap-xl-4 order-sm-1 order-lg-0 order-xl-1"
              >
                {/* 🔧 BOTONES FUNCIONALES CON ESTADOS DE LOADING */}
                <Button
                  variant="link"
                  className="p-1 text-body-tertiary"
                  onClick={() => handleEmailAction('reply')}
                  disabled={actionLoading !== null}
                  title="Responder"
                >
                  {actionLoading === 'reply' ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FontAwesomeIcon icon={faReply} />
                  )}
                </Button>
                
                <Button
                  variant="link"
                  className="p-1 text-body-tertiary"
                  onClick={() => handleEmailAction('delete')}
                  disabled={actionLoading !== null}
                  title="Eliminar"
                >
                  {actionLoading === 'delete' ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FontAwesomeIcon icon={faTrashCan} />
                  )}
                </Button>
                
                <Button
                  variant="link"
                  className="p-1 text-body-tertiary"
                  onClick={() => handleEmailAction('archive')}
                  disabled={actionLoading !== null}
                  title="Archivar"
                >
                  {actionLoading === 'archive' ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FontAwesomeIcon icon={faArchive} />
                  )}
                </Button>
                
                <Button
                  variant="link"
                  className="p-1 text-body-tertiary"
                  onClick={() => handleEmailAction('print')}
                  disabled={actionLoading !== null}
                  title="Imprimir"
                >
                  {actionLoading === 'print' ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FontAwesomeIcon icon={faPrint} />
                  )}
                </Button>
                
                <Button
                  variant="link"
                  className={`p-1 ${starred ? 'text-warning' : 'text-body-tertiary'}`}
                  onClick={() => handleEmailAction('star')}
                  disabled={actionLoading !== null}
                  title={starred ? "Quitar destacado" : "Destacar"}
                >
                  {actionLoading === 'star' ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FontAwesomeIcon icon={starred ? faStar : farStar} />
                  )}
                </Button>
              </Col>
              <Col xs="auto">
                <Avatar size="xl">
                  <div className="avatar-name rounded-circle">
                    <span>{getInitial(email.sender?.name || 'U')}</span> 
                  </div>
                </Avatar>
              </Col>
              <Col xs="auto" className="flex-1">
                <div className="d-flex mb-1">
                  <h5 className="mb-0 text-body-highlight me-2">
                    {email.sender?.name || 'Unknown Sender'}
                  </h5>
                  <p className="mb-0 lh-sm text-body-tertiary fs-9 d-none d-md-block text-nowrap">
                    &lt; {email.sender?.email || 'unknown@example.com'} &gt;
                  </p>
                </div>
                <p className="mb-0 fs-9">
                  <span className="text-body-tertiary me-1">to</span>
                  <span className="fw-bold text-body-secondary me-1">
                    {email.recipient?.to?.includes(getEmailConfig()?.username) ? 'Me' : (email.recipient?.to || 'N/A')}
                  </span>
                  <span className="text-body-highlight fw-semibold fs-10 me-1">
                    {email.formattedDate || 'N/A'}
                  </span>
                  <Button
                    variant="link"
                    className={`p-0 ${starred ? 'text-warning' : 'text-body-quaternary'}`}
                    onClick={() => handleEmailAction('star')}
                    disabled={actionLoading !== null}
                    style={{ border: 'none', background: 'none' }}
                    title={starred ? "Quitar destacado" : "Destacar"}
                  >
                    <FontAwesomeIcon icon={starred ? faStar : farStar} />
                  </Button>
                </p>
              </Col>
            </Row>
            
            {/* Email Body */}
            <div className="text-body-highlight fs-9 w-100 w-md-75 mb-8">
              {email.body.html ? (
                <div dangerouslySetInnerHTML={{ __html: email.body.html }} />
              ) : (
                <div style={{ whiteSpace: 'pre-wrap' }}>{email.body.text}</div>
              )}
            </div>

            {/* Attachments */}
            {email.attachments.length > 0 && (
              <>
                <div className="d-flex align-items-center mb-5">
                  <Button
                    variant="link"
                    className="text-body-highlight fs-8 text-decoration-none p-0"
                    type="button"
                    startIcon={
                      <FontAwesomeIcon icon={faPaperclip} className="me-2" />
                    }
                  >
                    {email.attachments.length} Attachment{email.attachments.length > 1 ? 's' : ''}
                  </Button>
                </div>
                <Row className="pb-11 border-bottom border-translucent mb-4 gx-0 gy-2">
                  {email.attachments.map((attachment) => (
                    <Col xs="auto" className="me-3" key={attachment.id}>
                      <AttachmentPreview
                        attachment={{
                          name: attachment.name,
                          size: attachment.size,
                          format: attachment.format
                        }}
                        size="xl"
                      />
                    </Col>
                  ))}
                </Row>
              </>
            )}

            {/* 🔧 BOTONES DE ACCIÓN PRINCIPALES CON FUNCIONALIDADES REALES */}
            <div className="d-flex justify-content-between">
              <Button
                variant="phoenix-secondary"
                className="me-1 text-nowrap px-2 px-sm-4"
                onClick={() => handleEmailAction('reply')}
                disabled={actionLoading !== null}
                endIcon={
                  actionLoading === 'reply' ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FontAwesomeIcon icon={faReply} className="ms-2 fs-10" />
                  )
                }
              >
                Responder
              </Button>
              <Button
                variant="phoenix-secondary"
                className="me-1 text-nowrap px-2 px-sm-4"
                onClick={() => handleEmailAction('reply-all')}
                disabled={actionLoading !== null}
                endIcon={
                  actionLoading === 'reply-all' ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FontAwesomeIcon icon={faReplyAll} className="ms-2 fs-10" />
                  )
                }
              >
                Responder a todos
              </Button>
              <Button
                variant="phoenix-secondary"
                className="ms-auto text-nowrap px-2 px-sm-4"
                onClick={() => handleEmailAction('forward')}
                disabled={actionLoading !== null}
                endIcon={
                  actionLoading === 'forward' ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FontAwesomeIcon icon={faShare} className="ms-2 fs-10" />
                  )
                }
              >
                Reenviar
              </Button>
            </div>
          </div>
        </Card.Body>
      </>
    );
  };

  // Si está embebido, no usar EmailLayout
  if (embedded) {
    return (
      <Card className="email-content h-100">
        {emailContent()}
      </Card>
    );
  }

  // Vista normal con EmailLayout
  return (
    <EmailLayout page="detail">
      <Col>
        <Card className="email-content">
          {emailContent()}
        </Card>
      </Col>
    </EmailLayout>
  );
};

export default EmailDetail;